import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import { useToast } from '@/components/base/Toast';
import { useAuth } from '@/hooks/useAuth';

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: 'admin' | 'cotizador' | 'contabilidad';
  activo: boolean;
  fecha_creacion: string;
}

interface UsuarioForm {
  nombre: string;
  email: string;
  password: string;
  rol: 'admin' | 'cotizador' | 'contabilidad';
}

const rolesDisponibles = [
  { value: 'admin' as const, label: 'Administrador', icon: 'ri-shield-user-line', color: 'bg-amber-100 text-amber-700' },
  { value: 'cotizador' as const, label: 'Cotizador', icon: 'ri-calculator-line', color: 'bg-green-100 text-green-700' },
  { value: 'contabilidad' as const, label: 'Contabilidad', icon: 'ri-bank-line', color: 'bg-accent-100 text-accent-700' },
];

const emptyForm: UsuarioForm = { nombre: '', email: '', password: '', rol: 'cotizador' };

function getRolBadge(rol: Usuario['rol']) {
  switch (rol) {
    case 'admin': return { label: 'Administrador', color: 'bg-amber-100 text-amber-800', icon: 'ri-shield-user-line' };
    case 'cotizador': return { label: 'Cotizador', color: 'bg-green-100 text-green-800', icon: 'ri-calculator-line' };
    case 'contabilidad': return { label: 'Contabilidad', color: 'bg-accent-100 text-accent-800', icon: 'ri-bank-line' };
  }
}

const API_URL = 'https://api.airmex.mx/api/v1';

export default function UsuariosPage() {
  const { showToast } = useToast();
  const { usuario } = useAuth(); // para asegurarnos de que sea admin (opcional, ya protegido en backend)
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroRol, setFiltroRol] = useState('todos');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<UsuarioForm>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<Usuario | null>(null);

  // Token almacenado
  const getToken = () => {
    return localStorage.getItem('airmex_token') || sessionStorage.getItem('airmex_token');
  };

  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };
    const response = await fetch(`${API_URL}${url}`, { ...options, headers });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Error desconocido' }));
      throw new Error(error.detail || 'Error en la petición');
    }
    return response.json();
  };

  // Cargar usuarios al montar
  const cargarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await fetchWithAuth('/usuarios/');
      setUsuarios(data);
    } catch (err: any) {
      showToast('error', 'Error al cargar usuarios', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarUsuarios();
  }, []);

  // Filtrado
  const filtered = useMemo(() =>
    usuarios.filter((u) => {
      const matchSearch = u.nombre.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRol = filtroRol === 'todos' || u.rol === filtroRol;
      const matchEstado = filtroEstado === 'todos' ||
        (filtroEstado === 'activo' && u.activo) ||
        (filtroEstado === 'inactivo' && !u.activo);
      return matchSearch && matchRol && matchEstado;
    }),
    [usuarios, search, filtroRol, filtroEstado]
  );

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (u: Usuario) => {
    setEditingId(u.id);
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.email.trim()) return;

    try {
      if (editingId) {
        // Actualizar
        const payload: any = { nombre: form.nombre, email: form.email, rol: form.rol };
        if (form.password.trim()) payload.password = form.password;
        const updated = await fetchWithAuth(`/usuarios/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setUsuarios(prev => prev.map(u => u.id === editingId ? updated : u));
        showToast('success', 'Usuario actualizado', `${form.nombre} se ha modificado.`);
      } else {
        if (!form.password.trim()) return;
        const nuevo = await fetchWithAuth('/usuarios/', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        setUsuarios(prev => [...prev, nuevo]);
        showToast('success', 'Usuario creado', `${form.nombre} se ha dado de alta.`);
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
    } catch (err: any) {
      showToast('error', 'Error al guardar', err.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await fetchWithAuth(`/usuarios/${deleteTarget.id}`, { method: 'DELETE' });
      setUsuarios(prev => prev.filter(u => u.id !== deleteTarget.id));
      showToast('success', 'Usuario eliminado', `${deleteTarget.nombre} se ha eliminado.`);
      setDeleteTarget(null);
    } catch (err: any) {
      showToast('error', 'Error al eliminar', err.message);
    }
  };

  const handleToggleActivo = async (u: Usuario) => {
    try {
      const updated = await fetchWithAuth(`/usuarios/${u.id}`, {
        method: 'PUT',
        body: JSON.stringify({ activo: !u.activo }),
      });
      setUsuarios(prev => prev.map(x => x.id === u.id ? updated : x));
      showToast(
        updated.activo ? 'success' : 'info',
        updated.activo ? 'Usuario activado' : 'Usuario desactivado',
        `${updated.nombre} ahora está ${updated.activo ? 'activo' : 'inactivo'}.`
      );
    } catch (err: any) {
      showToast('error', 'Error al cambiar estado', err.message);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-[1200px] mx-auto">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">Catálogo de Usuarios</h1>
            <p className="text-sm text-foreground-500 mt-0.5">{usuarios.length} usuarios registrados</p>
          </div>
          <button onClick={openCreate} className="px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line mr-1.5" />Nuevo usuario
          </button>
        </div>

        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o email..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <select value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)} className="max-w-[180px] px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 cursor-pointer">
            <option value="todos">Todos los roles</option>
            <option value="admin">Administrador</option>
            <option value="cotizador">Cotizador</option>
            <option value="contabilidad">Contabilidad</option>
          </select>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="max-w-[160px] px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100 cursor-pointer">
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>

        {/* Tabla */}
        <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-user-settings-line text-xl text-foreground-300" /></div>
              <p className="text-sm text-foreground-500">No se encontraron usuarios.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3">Usuario</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden md:table-cell">Rol</th>
                    <th className="text-left text-xs font-medium text-foreground-400 uppercase px-5 py-3 hidden lg:table-cell">Creado</th>
                    <th className="text-center text-xs font-medium text-foreground-400 uppercase px-5 py-3">Estado</th>
                    <th className="text-right text-xs font-medium text-foreground-400 uppercase px-5 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((u) => {
                    const badge = getRolBadge(u.rol);
                    return (
                      <tr key={u.id} className="hover:bg-background-50/50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${!u.activo ? 'bg-background-200' : 'bg-secondary-100'}`}>
                              <span className={`text-xs font-bold ${!u.activo ? 'text-foreground-400' : 'text-secondary-700'}`}>
                                {u.nombre.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${!u.activo ? 'text-foreground-400' : 'text-foreground-800'}`}>{u.nombre}</p>
                              <p className="text-[11px] text-foreground-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 hidden md:table-cell">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${badge.color}`}>
                            <i className={`${badge.icon} text-[10px]`} />
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 hidden lg:table-cell">
                          <span className="text-xs text-foreground-500">
                            {new Date(u.fecha_creacion).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <button
                            onClick={() => handleToggleActivo(u)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                              u.activo
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-foreground-100 text-foreground-500 hover:bg-foreground-200'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${u.activo ? 'bg-green-500' : 'bg-foreground-400'}`} />
                            {u.activo ? 'Activo' : 'Inactivo'}
                          </button>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openEdit(u)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Editar usuario">
                              <i className="ri-edit-line text-sm" />
                            </button>
                            <button onClick={() => setDeleteTarget(u)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Eliminar usuario">
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal crear/editar */}
      <Modal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setForm(emptyForm); setEditingId(null); }}
        title={editingId ? 'Editar usuario' : 'Nuevo usuario'}
        size="md"
        footer={
          <>
            <button onClick={() => { setModalOpen(false); setForm(emptyForm); setEditingId(null); }} className="px-4 py-2 rounded-lg border border-background-200 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handleSave} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!form.nombre.trim() || !form.email.trim() || (!editingId && !form.password.trim()) ? 'bg-background-200 text-foreground-400 cursor-not-allowed' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`} disabled={!form.nombre.trim() || !form.email.trim() || (!editingId && !form.password.trim())}>
              {editingId ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nombre completo *</label>
            <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Juan Pérez García" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Correo electrónico *</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Ej: juan.perez@construcciones.com" className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">
              Contraseña {editingId ? '(dejar vacío para no cambiar)' : '*'}
            </label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editingId ? '••••••••' : 'Mínimo 6 caracteres'} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-700 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-100" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-2">Rol del usuario</label>
            <div className="grid grid-cols-3 gap-2">
              {rolesDisponibles.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm({ ...form, rol: r.value })}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-xs transition-all cursor-pointer whitespace-nowrap ${
                    form.rol === r.value
                      ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
                      : 'border-background-200 bg-background-50 text-foreground-500 hover:border-background-300 hover:bg-background-100'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${r.icon} text-base`} />
                  </div>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirmación eliminar */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar usuario"
        message={`¿Estás seguro de eliminar a "${deleteTarget?.nombre}"? Esta acción no se puede deshacer y el usuario perderá el acceso al sistema.`}
        confirmLabel="Sí, eliminar"
        variant="danger"
      />
    </DashboardLayout>
  );
}